import {
    ArrowsPointingInIcon,
    ArrowsPointingOutIcon,
    ArrowsRightLeftIcon
} from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';
import imageIcon from './assets/image.svg';
import videoIcon from './assets/video.svg';

export type MediaView = 'photos' | 'morphing' | 'compare';

interface MediaToggleOption {
    view: MediaView;
    label: string;
    icon: ReactNode;
}

const MEDIA_TOGGLE_OPTIONS: MediaToggleOption[] = [
    { view: 'photos', label: 'Photos', icon: <img src={imageIcon} alt="" className="w-4 h-4" /> },
    {
        view: 'morphing',
        label: 'Smile Morph',
        icon: <img src={videoIcon} alt="" className="w-4 h-4" />
    },
    { view: 'compare', label: 'Compare', icon: <ArrowsRightLeftIcon className="w-4 h-4" /> }
];

interface MediaToggleButtonProps {
    option: MediaToggleOption;
    isActive: boolean;
    onSelect: (view: MediaView) => void;
}

function MediaToggleButton({ option, isActive, onSelect }: MediaToggleButtonProps) {
    return (
        <button
            type="button"
            onClick={() => onSelect(option.view)}
            aria-pressed={isActive}
            className={`flex-1 rounded-md px-3 py-2 flex items-center justify-center gap-2 text-sm font-medium transition-colors ${
                isActive
                    ? 'bg-white border border-gray-300 shadow-sm text-gray-900'
                    : 'border border-transparent text-gray-600 hover:text-gray-800'
            }`}
        >
            {option.icon}
            {option.label}
        </button>
    );
}

export interface MediaToggleBarProps {
    mediaView: MediaView;
    availableViews: readonly MediaView[];
    onSelect: (view: MediaView) => void;
    /** Omit to hide the expand control entirely. */
    onToggleFullscreen?: () => void;
    isFullscreen?: boolean;
}

function MediaToggleBar({
    mediaView,
    availableViews,
    onSelect,
    onToggleFullscreen,
    isFullscreen = false
}: MediaToggleBarProps) {
    return (
        <div className="mt-3 w-full bg-gray-100 border border-gray-200 rounded-xl p-1 shrink-0">
            <div className="flex w-full gap-1">
                {MEDIA_TOGGLE_OPTIONS.filter((option) => availableViews.includes(option.view)).map(
                    (option) => (
                        <MediaToggleButton
                            key={option.view}
                            option={option}
                            isActive={mediaView === option.view}
                            onSelect={onSelect}
                        />
                    )
                )}
                {onToggleFullscreen && (
                    <button
                        type="button"
                        onClick={onToggleFullscreen}
                        aria-pressed={isFullscreen}
                        title={isFullscreen ? 'Exit full screen' : 'Full screen'}
                        aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'}
                        className="shrink-0 rounded-md px-3 py-2 flex items-center justify-center border border-transparent text-gray-600 hover:text-gray-800 hover:bg-white/70 transition-colors"
                    >
                        {isFullscreen ? (
                            <ArrowsPointingInIcon className="w-4 h-4" />
                        ) : (
                            <ArrowsPointingOutIcon className="w-4 h-4" />
                        )}
                    </button>
                )}
            </div>
        </div>
    );
}

export { MediaToggleBar };
